import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Server, Package } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import { projects, futureProjects } from '../../data/projects';

const ProjectsPage: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-theme font-bold mb-6 text-gradient">
              Our Projects
            </h1>
            <p className="text-xl md:text-2xl" style={{ color: 'var(--color-text-secondary)' }}>
              The stuff we actually made (and the stuff we forked because we needed it to work)
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-12">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="p-8 rounded-2xl"
                style={{
                  backgroundColor: 'var(--color-bg-secondary)',
                  borderColor: project.featured ? 'var(--color-primary)' : 'var(--color-border)',
                  borderWidth: project.featured ? '2px' : '1px',
                  borderStyle: 'solid'
                }}
              >
                {project.featured && (
                  <div className="inline-block px-4 py-1 rounded-full mb-4 text-sm font-semibold" style={{
                    background: 'var(--gradient-primary)',
                    color: 'var(--color-bg)'
                  }}>
                    ⭐ Featured Project
                  </div>
                )}

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                  <div className="flex-1">
                    <h2 className="text-3xl md:text-4xl font-theme font-bold mb-2" style={{ color: 'var(--color-primary)' }}>
                      {project.name}
                    </h2>
                    <p className="text-xl font-semibold mb-4 italic" style={{ color: 'var(--color-text-secondary)' }}>
                      {project.tagline}
                    </p>

                    <div className="space-y-3 mb-6">
                      {project.longDescription.map((paragraph, i) => (
                        <p key={i} style={{ color: 'var(--color-text)' }}>
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.technologies.map(tech => (
                        <span
                          key={tech}
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: 'var(--color-primary)' + '20',
                            color: 'var(--color-primary)'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Status Badge */}
                    <div className="mb-6">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                        style={{
                          backgroundColor: project.status === 'active' ? 'var(--color-success)' + '20' :
                                         project.status === 'maintenance' ? 'var(--color-warning)' + '20' :
                                         'var(--color-text-secondary)' + '20',
                          color: project.status === 'active' ? 'var(--color-success)' :
                                 project.status === 'maintenance' ? 'var(--color-warning)' :
                                 'var(--color-text-secondary)'
                        }}
                      >
                        {project.status === 'active' ? '🟢 Active Development' :
                         project.status === 'maintenance' ? '🟡 Maintenance Mode' :
                         '⚫ Archived'}
                      </span>
                    </div>

                    {/* Links */}
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={project.githubLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                        style={{
                          backgroundColor: 'var(--color-primary)',
                          color: 'var(--color-bg)'
                        }}
                      >
                        <Github size={20} />
                        <span>GitHub</span>
                        <ExternalLink size={16} />
                      </a>

                      {project.dockerLink && (
                        <a
                          href={project.dockerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:scale-105"
                          style={{
                            borderColor: 'var(--color-primary)',
                            color: 'var(--color-primary)',
                            backgroundColor: 'var(--color-primary)' + '10'
                          }}
                        >
                          <Server size={20} />
                          <span>Docker Hub</span>
                          <ExternalLink size={16} />
                        </a>
                      )}

                      {project.demoLink && (
                        <a
                          href={project.demoLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:scale-105"
                          style={{
                            borderColor: 'var(--color-primary)',
                            color: 'var(--color-primary)',
                            backgroundColor: 'var(--color-primary)' + '10'
                          }}
                        >
                          <Package size={20} />
                          <span>Demo</span>
                          <ExternalLink size={16} />
                        </a>
                      )}

                      {project.link && project.link.startsWith('/') && (
                        <Link
                          to={project.link}
                          className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium border-2 transition-all hover:scale-105"
                          style={{
                            borderColor: 'var(--color-primary)',
                            color: 'var(--color-primary)',
                            backgroundColor: 'var(--color-primary)' + '10'
                          }}
                        >
                          <Package size={20} />
                          <span>Learn More</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Projects (The "Probably Never Happening" Section) */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-theme font-bold mb-4 text-center text-gradient">
              Future Projects
            </h2>
            <p className="text-center mb-8 italic" style={{ color: 'var(--color-text-secondary)' }}>
              (The "Probably Never Happening" List)
            </p>

            <div
              className="p-8 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              <ul className="space-y-3">
                {futureProjects.map((project, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="flex items-start space-x-3"
                  >
                    <span style={{ color: 'var(--color-primary)' }}>▸</span>
                    <span style={{ color: 'var(--color-text)' }}>{project}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <p className="text-center mt-6 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Have an idea? Open an issue on GitHub. We probably won't do it, but we'll appreciate the thought.
            </p>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ProjectsPage;
